<template>
  <div class="widget-body" v-resize:debounce.50="onResize" tabindex="-1">
    <v-alert show v-if="!ric" variant="light" align="center">{{ i18n('messages.noRic') }}</v-alert>
    <c-loader v-else-if="isLoading" />
    <v-alert show v-else-if="isError" variant="red" align="center">{{ i18n('messages.error') }}</v-alert>
    <v-alert show v-else-if="!data || !data['q.RIC']" variant="light" align="center">{{
      i18n('messages.nodata')
    }}</v-alert>
    <template v-else-if="data['q.RIC']" v-show="!isLoading">
      <div class="charts-holder">
        <div v-for="col in 2" :key="col">
          <div v-for="table in getTablenamesSubset(col)" :key="table">
            <h6 :style="{ borderColor: dataMap[table][selection[table]].color }" aria-hidden="true">
              {{ i18n(`tables.${table}.rows.${selection[table]}`) }}
              <template v-if="dataMap[table][selection[table]].currency"
                >({{ data['q._CURRENCY'] }})</template
              >
            </h6>
            <highcharts :options="chartData[table]" ref="charts" class="chart" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <b-table-simple
          dark
          striped
          bordered
          v-for="tableName in Object.keys(dataMap)"
          :key="tableName"
          :aria-label="i18n(`tables.${tableName}.label`)"
          @keydown.native="onKeyDown"
          @click.native="onClick"
        >
          <b-tr role="col">
            <b-th
              :id="tableName"
              aria-colindex="1"
              tabindex="0"
              role="columnheader"
              headers="blank"
              scope="col"
              class="theader"
              >{{ i18n(`tables.${tableName}.label`) }}</b-th
            >
            <b-th
              :id="tableName"
              :aria-colindex="index + 2"
              tabindex="0"
              role="columnheader"
              headers="blank"
              scope="col"
              v-for="(year, index) in years"
              :key="year"
            >
              <v-fidlet :data="`${year}/12/31` || ''" type="date.localed" />
            </b-th>
          </b-tr>
          <b-tbody>
            <b-tr
              v-for="(row, label) in dataMap[tableName]"
              :key="label"
              :class="{ selected: selection[tableName] === label }"
              @click="$emit('selection-changed', { tableName, label })"
            >
              <b-td
                :headers="tableName"
                aria-colindex="1"
                tabindex="0"
                @keydown.enter="$emit('selection-changed', { tableName, label })"
              >
                <div class="marker" :style="{ backgroundColor: row.color }"></div>
                <span v-show="label === selection[tableName]">
                  <i class="fas fa-dot-circle"></i>
                  <span class="sr-only" :v-if="label === selection[tableName]">{{ i18n(`tables.selected`) }}</span>
                </span>
                <span v-show="label !== selection[tableName]">
                  <i class="far fa-circle"></i>
                  <span class="sr-only" :v-if="label !== selection[tableName]">{{ i18n(`tables.unselected`) }}</span>
                </span>
                {{ i18n(`tables.${tableName}.rows.${label}`) }}
              </b-td>
              <b-td
                :headers="tableName"
                v-for="(val, index) in mapYearsToData(label, row.values)"
                :key="index"
                :class="getCellClass(label, val)"
                :aria-colindex="index + 2"
                tabindex="0"
              >
                {{ formatCell(label, val) }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </template>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import HighchartsVue from 'highcharts';

import { API } from '@/state/api';
import styleVars from '@mm5-components/scss/variables.scss';
import resize from 'vue-resize-directive';

export default {
  name: 'w-estimates-ratios-widget',
  components: {
    CLoader,
    HighchartsVue
  },
  directives: {
    resize
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      type: String,
      required: true
    },
    locale: {
      type: String,
      required: true
    },
    fontSize: {
      type: String,
      required: true
    },
    isFontBold: {
      type: Boolean,
      required: true
    },
    selection: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      data: {},
      dataMap: {
        earnings: {
          eps: {
            color: '#79A5EE',
            currency: true,
            values: ['rkd.EPS_LST2Y', 'rkd.EPS_LST1Y', 'rkd.EPS', 'rkd.EPS_NXT1Y', 'rkd.EPS_NXT2Y', 'rkd.EPS_NXT3Y']
          },
          'eps-change': {
            color: '#5873FF',
            values: [
              'rkd.EPS_CHANGE_LST2Y_TO_LST3Y',
              'rkd.EPS_CHANGE_LST1Y_TO_LST2Y',
              'rkd.EPS_CHANGE_CURRY_TO_LST1Y',
              'rkd.EPS_CHANGE_NXT1Y_TO_CURRY',
              'rkd.EPS_CHANGE_NXT2Y_TO_NXT1Y',
              'rkd.EPS_CHANGE_NXT3Y_TO_NXT2Y'
            ]
          },
          'earnings-yield': {
            color: '#1E40BE',
            values: [
              'rkd.EPS_TO_PRICE_LST2Y',
              'rkd.EPS_TO_PRICE_LST1Y',
              'rkd.EPS_TO_PRICE',
              'rkd.EPS_TO_PRICE_NXT1Y',
              'rkd.EPS_TO_PRICE_NXT2Y',
              'rkd.EPS_TO_PRICE_NXT3Y'
            ]
          }
        },
        'price-earnings-ratio': {
          'price-earnings-ratio': {
            color: '#B37EC4',
            values: [
              'rkd.PRICE_TO_EPS_LST2Y',
              'rkd.PRICE_TO_EPS_LST1Y',
              'rkd.PRICE_TO_EPS',
              'rkd.PRICE_TO_EPS_NXT1Y',
              'rkd.PRICE_TO_EPS_NXT2Y',
              'rkd.PRICE_TO_EPS_NXT3Y'
            ]
          },
          'peg-ratio': {
            color: '#B103A2',
            values: ['rkd.PEG_LST2Y', 'rkd.PEG_LST1Y', 'rkd.PEG', 'rkd.PEG_NXT1Y', 'rkd.PEG_NXT2Y', 'rkd.PEG_NXT3Y']
          }
        },
        'cash-flow': {
          'cash-flow-share': {
            color: '#DCB86C',
            currency: true,
            values: ['rkd.CPS_LST2Y', 'rkd.CPS_LST1Y', 'rkd.CPS', 'rkd.CPS_NXT1Y', 'rkd.CPS_NXT2Y', 'rkd.CPS_NXT3Y']
          },
          'price-cash-flow': {
            color: '#F48F03',
            values: [
              'rkd.PRICE_TO_CPS_LST2Y',
              'rkd.PRICE_TO_CPS_LST1Y',
              'rkd.PRICE_TO_CPS',
              'rkd.PRICE_TO_CPS_NXT1Y',
              'rkd.PRICE_TO_CPS_NXT2Y',
              'rkd.PRICE_TO_CPS_NXT3Y'
            ]
          }
        },
        market: {
          'dividend-yield': {
            color: '#95D49B',
            values: [
              'rkd.DPS_TO_PRICE_LST2Y',
              'rkd.DPS_TO_PRICE_LST1Y',
              'rkd.DPS_TO_PRICE',
              'rkd.DPS_TO_PRICE_NXT1Y',
              'rkd.DPS_TO_PRICE_NXT2Y',
              'rkd.DPS_TO_PRICE_NXT3Y'
            ]
          },
          bvps: {
            color: '#41BD27',
            currency: true,
            values: [
              'rkd.BVPS_LST2Y',
              'rkd.BVPS_LST1Y',
              'rkd.BVPS',
              'rkd.BVPS_NXT1Y',
              'rkd.BVPS_NXT2Y',
              'rkd.BVPS_NXT3Y'
            ]
          },
          'book-value': {
            color: '#09A900',
            currency: true,
            values: ['rkdp.BOOKVALUE_LST2Y', 'rkdp.BOOKVALUE_LST1Y', 'rkdp.BOOKVALUE']
          },
          'price-book-ratio': {
            color: '#08AE6C',
            values: [
              'rkd.PRICE_TO_BVPS_LST2Y',
              'rkd.PRICE_TO_BVPS_LST1Y',
              'rkd.PRICE_TO_BVPS',
              'rkd.PRICE_TO_BVPS_NXT1Y',
              'rkd.PRICE_TO_BVPS_NXT2Y',
              'rkd.PRICE_TO_BVPS_NXT3Y'
            ]
          },
          'market-value': {
            color: '#2C845E',
            currency: true,
            values: ['rkdp.MARKETVALUE_LST2Y', 'rkdp.MARKETVALUE_LST1Y', 'rkdp.MARKETVALUE']
          },
          'market-book-ratio': {
            color: '#00A3AA',
            values: [
              'rkdp.MARKETVALUE_TO_BOOKVALUE_LST2Y',
              'rkdp.MARKETVALUE_TO_BOOKVALUE_LST1Y',
              'rkdp.MARKETVALUE_TO_BOOKVALUE'
            ]
          },
          ebitda: {
            color: '#00CAEF',
            currency: true,
            values: [
              'rkdp.EBITDA_LST2Y',
              'rkdp.EBITDA_LST1Y',
              'rkdp.EBITDA',
              'rkdp.EBITDA_NXT1Y',
              'rkdp.EBITDA_NXT2Y',
              'rkdp.EBITDA_NXT3Y'
            ]
          },
          'market-value-ebitda': {
            color: '#00E6A2',
            values: [
              'rkdp.MARKETVALUE_TO_EBITDA_LST2Y',
              'rkdp.MARKETVALUE_TO_EBITDA_LST1Y',
              'rkdp.MARKETVALUE_TO_EBITDA'
            ]
          },
          'ebitda-sales': {
            color: '#54FACA',
            values: ['rkdp.EBITDA_TO_NET_SALES_LST2Y', 'rkdp.EBITDA_TO_NET_SALES_LST1Y', 'rkdp.EBITDA_TO_NET_SALES']
          }
        }
      },
      dateFids: [
        'rkdp.FINYEAREND_DATE_LST2Y',
        'rkdp.FINYEAREND_DATE_LST1Y',
        'rkdp.FINYEAREND_DATE',
        'rkdp.FINYEAREND_DATE_NXT1Y',
        'rkdp.FINYEAREND_DATE_NXT2Y',
        'rkdp.FINYEAREND_DATE_NXT3Y'
      ],
      isError: false,
      isLoading: false,
      percentEntries: ['eps-change', 'earnings-yield', 'dividend-yield']
    };
  },
  watch: {
    fontSize() {
      this.rerenderCharts();
    },
    fontWeight() {
      this.rerenderCharts();
    },
    ric: {
      handler() {
        this.fetch();
      },
      immediate: true
    },
    locale: {
      handler() {
        this.rerenderCharts();
      },
      immediate: true
    }
  },
  computed: {
    baseChartData() {
      return {
        accessibility: {
          enabled: false
        },
        chart: {
          type: 'column',
          animation: true,
          spacing: [
            10,
            10,
            Math.min(7, 10 - (parseInt(styleVars['fontSize' + this.fontSize.toUpperCase()]) - 11) * 2),
            10
          ]
          // styledMode: true
        },
        title: {
          text: ''
        },
        tooltip: {
          enabled: false
        },
        xAxis: {
          tickPixelInterval: 60 + (parseInt(styleVars['fontSize' + this.fontSize.toUpperCase()]) - 11) * 6,
          labels: {
            autoRotation: false,
            style: {
              fontSize: styleVars['fontSize' + this.fontSize.toUpperCase()],
              fontWeight: this.isFontBold ? 'bold' : 'normal'
            },
            y: 15 + (parseInt(styleVars['fontSize' + this.fontSize.toUpperCase()]) - 11) * 2
          }
        },
        yAxis: {
          title: {
            enabled: false
          },
          tickAmount: 6,
          labels: {
            style: {
              fontSize: styleVars['fontSize' + this.fontSize.toUpperCase()],
              fontWeight: this.isFontBold ? 'bold' : 'normal'
            }
          }
        },
        plotOptions: {
          series: {
            borderColor: '',
            minPointLength: 1
          }
        },
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        }
      };
    },
    years() {
      return this.dateFids.map(fid => {
        return parseInt(this.data[fid]);
      });
    },
    fids() {
      const fids = ['q.RIC', 'q._CURRENCY', ...this.dateFids];
      Object.keys(this.dataMap).forEach(table => {
        Object.keys(this.dataMap[table]).forEach(row => {
          fids.push(...this.dataMap[table][row].values);
        });
      });
      return fids;
    },
    chartData() {
      return Object.keys(this.dataMap).reduce((data, table) => {
        const entryName = this.selection[table];
        const chartOptions = JSON.parse(JSON.stringify(this.baseChartData));
        chartOptions.series = [
          {
            data: this.mapYearsToData(entryName, this.dataMap[table][entryName].values, true),
            color: this.dataMap[table][entryName].color
          }
        ];
        const vm = this;
        chartOptions.yAxis.labels.formatter = function() {
          return this.axis.defaultLabelFormatter.call(this) + (vm.percentEntries.includes(entryName) ? '%' : '');
        };
        data[table] = chartOptions;
        return data;
      }, {});
    }
  },
  methods: {
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.estimatesRatios'}.${subkey}`);
    },
    getTablenamesSubset(col) {
      return Object.keys(this.dataMap).slice((col - 1) * 2, (col - 1) * 2 + 2);
    },
    getCellClass(label, val) {
      if (label === 'eps-change') {
        const numval = parseFloat(val);
        return numval ? (numval < 0 ? 'negative' : 'positive') : '';
      }
      return '';
    },
    formatCell(label, val) {
      if (['book-value', 'market-value', 'ebitda'].includes(label) && this._.isFinite(val)) {
        return (
          this.$n(val, {
            useGrouping: this.$store.getters['applicationSettings/commaSeparator']
          }) +
          ' ' +
          this.i18n('short.millions', 'globalLabels')
        );
      }
      return val;
    },
    mapYearsToData(entry, fids, isChartData) {
      if (isChartData) {
        return this.years.reduce((data, year, i) => {
          let val = parseFloat(this.data[fids[i]]);
          if (!this._.isFinite(val)) {
            val = 0;
          }
          if (['book-value', 'market-value', 'ebitda'].includes(entry)) {
            val *= 1000000;
          }
          data.push([parseInt(year), val || null]); // null to disable bar rendering for 0 values, which was activated by baseChartData.plotOptions.series.minPointLength
          return data;
        }, []);
      }
      return this.years.reduce((vals, year, i) => {
        let val = typeof this.data[fids[i]] === 'undefined' ? '-' : this.data[fids[i]];
        if (val !== '-') {
          let factor;
          switch (entry) {
            case 'eps-change':
              val = this.$f(val, 'float.price.percentage.change');
              break;
            case 'earnings-yield':
            case 'dividend-yield':
              val = this.$f(val, 'float.price.percentage');
              break;
            case 'book-value':
            case 'market-value':
            case 'ebitda':
              factor = Math.pow(10, 5 - String(val).match(/\d+/)[0].length);
              val = Math.round(val * factor) / factor;
              break;
            default:
              val = this.$f(val, 'float');
          }
        }
        vals.push(val);
        return vals;
      }, []);
    },
    fetch() {
      this.isLoading = true;
      API.Rest.get('/quote/info' + '?rics=' + this.ric + '&fids=' + this.fids).then(
        response => {
          this.isError = false;
          this.data = response.data.data[0];
          this.isLoading = false;
        },
        () => {
          this.isError = true;
          this.isLoading = false;
        }
      );
    },
    onResize() {
      this.rerenderCharts(true);
    },
    rerenderCharts(isReflow) {
      if (!this.$refs.charts) return;
      this.$nextTick(() => {
        this.$refs.charts.forEach(chart => {
          if (isReflow) chart.chart.reflow();
          else chart.chart.setSize(null, null); // rerender charts after locale change (labels size change) - redraw/reflow/update methods don't work here, setSize does
        });
      });
    },
    onKeyDown(event) {
      const currentCell = document.activeElement;

      const currentRow = currentCell.closest('tr');
      const currentTable = currentRow.closest('table');
      const currentPosition = currentCell.getAttribute('aria-colindex');

      let nextTable = null;
      let nextCell = null;
      let nextRow = null;

      switch (event.key) {
        case 'Tab':
          nextRow = event.shiftKey ? currentRow.previousElementSibling : currentRow.nextElementSibling;
          if (nextRow) nextCell = nextRow.firstChild;
          else {
            if (event.shiftKey) {
              nextTable = currentTable.previousElementSibling;
              if (!nextTable) {
                this.focusCloseButton(event);
                return;
              }
              nextRow = nextTable.querySelector('tbody').lastChild;
              nextCell = nextRow.firstChild;
            } else {
              nextTable = currentTable.nextElementSibling;
              if (nextTable) {
                nextRow = nextTable.querySelector('tbody').firstChild;
                nextCell = nextRow.firstChild;
              } else {
                currentRow.querySelectorAll('td').forEach(el => el.setAttribute('tabindex', '-1'));
                currentCell.addEventListener('blur', this.onBlur);
                return;
              }
            }
          }
          break;
        case 'ArrowRight':
          nextCell = currentCell.nextElementSibling;
          break;
        case 'ArrowLeft':
          nextCell = currentCell.previousElementSibling;
          break;
        case 'ArrowDown':
          nextRow = currentRow.nextElementSibling;
          if (nextRow) nextCell = nextRow.querySelector(`td[aria-colindex="${currentPosition}"]`);
          else {
            nextTable = currentTable.nextElementSibling;
            if (nextTable) {
              nextCell = nextTable.querySelector(`th[aria-colindex="${currentPosition}"]`);
            }
          }
          break;
        case 'ArrowUp':
          nextRow = currentRow.previousElementSibling;
          if (nextRow) nextCell = nextRow.querySelector(`td[aria-colindex="${currentPosition}"]`);
          else if (currentRow.parentElement.tagName === 'TBODY')
            nextCell = currentTable.querySelector(`th[aria-colindex="${currentPosition}"]`);
          else {
            nextTable = currentTable.previousElementSibling;
            if (!nextTable) return;
            nextRow = nextTable.querySelector('tbody').lastChild;
            nextCell = nextRow.querySelector(`td[aria-colindex="${currentPosition}"]`);
          }
          break;
        default:
          return;
      }

      if (nextCell) {
        nextCell.focus();
        nextCell.scrollIntoView({ behavior: 'instant', block: 'center' });
      }
      event.preventDefault();
    },
    onBlur(event) {
      event.target.parentElement.querySelectorAll('td').forEach(el => el.setAttribute('tabindex', '0'));
    },
    onClick(event) {
      event.target.focus();
    },
    focusCloseButton(e) {
      let cellElement = this.$el.parentElement.querySelector('.close-button');
      if (cellElement) cellElement.focus();
      e.preventDefault();
    }
  },
  beforeDestroy() {
    document.activeElement.removeEventListener('blur', this.onBlur);
  }
};
</script>
